/** /regions — licensee-scoped Region catalog (grid + drag reorder). */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip, CircularProgress, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as regionsApi from '@/api/regions';
import type { RegionListItem } from '@/api/regions';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { RegionCreateDialog } from './RegionCreateDialog';

const passthrough = (v: string) => v || undefined;

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'card_arrangement', toApi: passthrough },
];
/** The filters other than the tenant: any of them set hides rows of the one
 *  tenant's sequence, so drag reorder is off. */
const NON_TENANT_FILTERS = FILTERS.filter((f) => !f.tenantScope);

const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

const DEFAULT_ORDER = [
  'id',
  'tenant',
  'name',
  'card_arrangement',
  'countries_count',
  'latitude',
  'longitude',
  'sequence',
];

/** Columns present but hidden until toggled on via the column menu. */
const DEFAULT_HIDDEN = ['latitude', 'longitude', 'custom_id'];

export function RegionsListPage() {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const canManage = hasPerm('console_regions_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'regions',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'sequence',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what the create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const tenantId = singleTenantId(pageTenants.map((t) => t.id));
  const cardArrangement = getParam('card_arrangement') ?? 'all';

  // Drag reorder is only coherent when a single tenant's rows are shown in
  // their stored `sequence` order with nothing else filtered out. The tenant
  // filter itself is the precondition, not an obstacle, so it does not count.
  const orderingIsSequence = presets.ordering === 'sequence';
  const otherFilterActive = NON_TENANT_FILTERS.some((f) => getParam(f.param));
  const reorderEnabled = canManage && orderingIsSequence && !otherFilterActive && tenantId !== null;

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: number[]) => regionsApi.reorder(tenantId!, orderedIds),
    onSuccess: () => {
      snackbar.show('Order saved.', 'success');
      qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Reorder failed.', 'error'),
  });

  const columns = useMemo<GridColDef<RegionListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      // custom_id is serialized, searchable, AND in the backend ordering whitelist,
      // but had no column (the search box even advertises it). Mono + copyable, hidden.
      copyableColumn({ field: 'custom_id', headerName: 'Custom ID', width: 140 }),
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/regions/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'card_arrangement',
        headerName: 'Card arrangement',
        width: 160,
        sortable: false,
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            label={p.row.card_arrangement_display || p.row.card_arrangement}
          />
        ),
      },
      {
        field: 'countries_count',
        headerName: 'Countries',
        width: 110,
        sortable: false,
        valueGetter: (_, row) => row.countries_count,
      },
      {
        field: 'latitude',
        headerName: 'Latitude',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.latitude,
        renderCell: (p) => (p.row.latitude != null ? p.row.latitude : '—'),
      },
      {
        field: 'longitude',
        headerName: 'Longitude',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.longitude,
        renderCell: (p) => (p.row.longitude != null ? p.row.longitude : '—'),
      },
      { field: 'sequence', headerName: 'Seq', width: 90 },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      regionsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'sequence',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'card_arrangement',
      priority: 4,
      active: cardArrangement !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Card arrangement"
          value={cardArrangement}
          sx={{ width: 170 }}
          onChange={(e) =>
            setParam('card_arrangement', e.target.value === 'all' ? null : e.target.value)
          }
        >
          <MenuItem value="all">Any arrangement</MenuItem>
          <MenuItem value="horizontal">Horizontal</MenuItem>
          <MenuItem value="vertical">Vertical</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="regions-trip-types"
        title="Regions"
        subtitle="Licensee-scoped region catalog. Drag to reorder within an operator (sorted by sequence)."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New region
            </Button>
          )
        }
      />

      <RegionCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<RegionListItem>
          queryKey={['regions']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="regions"
          batchActions={[deleteBatchAction('console_regions_manage'), batchEditAction('regions', 'region')]}
          defaultSort={{ field: 'sequence', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          rowReordering={reorderEnabled}
          {...(reorderEnabled
            ? { onRowOrderChange: (ids) => reorderMutation.mutate(ids as number[]) }
            : {})}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name or custom ID',
            exportFileName: 'regions',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
